import { motion } from "framer-motion";
import { ShieldCheck, Users, TrendingUp, Heart, ArrowUpRight } from "lucide-react";

const CHECKS = [
  { icon: ShieldCheck, title: "RECENCY CHECK", body: "Is the momentum current, not stale.", id: "recency" },
  { icon: Users, title: "FAN GROWTH", body: "Listeners and followers both trending up — not just one.", id: "fan-growth" },
  { icon: TrendingUp, title: "LIFETIME TREND", body: "Sustained growth vs. a short-lived spike.", id: "lifetime" },
  { icon: Heart, title: "ENGAGEMENT DEPTH", body: "Real listener-to-follower conversion, not a shallow audience.", id: "engagement" },
];

const TIERS = [
  { name: "STANDARD REPORT", price: "$150–$300", note: "2–3 DAY TURNAROUND", id: "standard" },
  { name: "RUSH // SAME-DAY", price: "$300–$500", note: "SAME-DAY WHEN REQUESTED EARLY", id: "rush" },
  { name: "VOLUME // 3+ ARTISTS", price: "$250", note: "PER ARTIST, ONE BATCH", id: "volume" },
];

export default function AuditSection() {
  return (
    <section id="audit" className="relative py-28 sm:py-36" data-testid="audit-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mono text-[11px] tracking-[0.35em] text-[#00F0FF] mb-4" data-testid="audit-eyebrow">
            DATA-BACKED ARTIST AUDIT REPORTS // CHARTMETRIC + AIDAR.AI
          </p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="audit-heading">
            GET A SECOND <span className="text-stroke-cyan">OPINION</span> BEFORE YOU SIGN
          </h2>
          <p className="mt-8 text-sm sm:text-base text-[#A0A0AB] leading-relaxed max-w-lg">
            Before you spend money or sign a deal on an artist, get an independent,
            data-driven read on whether the growth is real. Trends pulled from two
            independent data sources, scored against a pass/fail system built to catch
            shallow or short-lived hype — <span className="text-[#F2F2F5]">before it costs you</span>.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-[#22222B] border border-[#22222B]">
            {CHECKS.map((c) => (
              <div key={c.id} className="bg-[#0C0C10] p-6 group hover:bg-[#111115] transition-colors duration-500" data-testid={`audit-check-${c.id}`}>
                <c.icon className="h-5 w-5 text-[#00F0FF] mb-4 group-hover:text-[#CCFF00] transition-colors duration-500" />
                <p className="font-display uppercase text-base tracking-wide mb-2">{c.title}</p>
                <p className="text-xs text-[#A0A0AB] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <a
            href="#contact"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("prefill-contact", {
                  detail: {
                    service: "Audit Report",
                    message: "I'd like a Second Opinion audit report.\nArtist to audit: \nStandard / Rush / Volume: \nDeadline: ",
                  },
                })
              )
            }
            className="group mt-10 inline-flex items-center gap-2 mono text-xs tracking-[0.2em] px-7 py-4 bg-[#00F0FF] text-black font-semibold hover:bg-[#CCFF00] transition-colors duration-300"
            data-testid="audit-cta-button"
          >
            GET AN AUDIT
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="tech-corner border border-[#22222B] overflow-hidden group mb-6" data-testid="audit-flyer">
            <img
              src="/assets/audit-flyer.png"
              alt="Second Opinion audit report flyer — Don't sign blind, sign smart"
              loading="lazy"
              className="w-full max-h-[420px] object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700"
            />
          </div>

          <div className="space-y-px bg-[#22222B] border border-[#22222B]">
            {TIERS.map((t) => (
              <div
                key={t.id}
                className="bg-[#0C0C10] px-6 py-5 flex items-center justify-between gap-4 hover:bg-[#111115] transition-colors duration-500"
                data-testid={`audit-tier-${t.id}`}
              >
                <div>
                  <p className="mono text-[10px] tracking-[0.25em] text-[#60606E]">{t.name}</p>
                  <p className="mono text-[10px] tracking-[0.15em] text-[#A0A0AB] mt-1">{t.note}</p>
                </div>
                <p className="font-display text-2xl sm:text-3xl text-[#CCFF00]">{t.price}</p>
              </div>
            ))}
          </div>
          <p className="mono text-[10px] tracking-[0.2em] text-[#60606E] mt-4 leading-relaxed" data-testid="audit-intro-note">
            INTRODUCTORY RATE: FIRST CLIENTS GET THE LOWER END IN EXCHANGE FOR A TESTIMONIAL.
          </p>
          <p className="font-display uppercase text-2xl sm:text-3xl mt-8 text-[#F2F2F5]" data-testid="audit-tagline">
            DON'T SIGN BLIND. <span className="text-[#00F0FF]">SIGN SMART.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
